import { Container, TextField, Button } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Post } from "../../../__generated__/graphql";
import { CREATE_POST } from "../graphql";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { PageContainer } from "../../../components/PageContainer";
import { FieldContainer } from "../../../components/FieldContainer";
import { ButtonContainer } from "../../../components/ButtonContainer";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Note } from "../../../components/Note";
import { SocialLinks, newsPostDefaults as defaultSocials } from "../SocialLinks";
import dayjs from "dayjs";
import { ZonedDateTimePicker } from "../../../components/ZonedDateTimePicker";
import { ErrorRenderer } from "../../../components/ErrorRenderer";
import { ImagePreview } from "../../../components/ImagePreview";

const schema = yup
  .object({
    title: yup.string().required(),
    description: yup.string().required(),
    date: yup.string().required(),
    socials: yup.object({
      facebook: yup.string().url().optional(),
      tiktok: yup.string().url().optional(),
      twitter: yup.string().url().optional(),
      instagram: yup.string().url().optional(),
    }),
  })
  .required();

export const PostForm = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      date: dayjs(new Date()).format("YYYY-MM-DD hh:mm"),
      socials: defaultSocials,
    },
  });
  const [createPost, { loading: updateLoading, error }] = useMutation(
    CREATE_POST,
    {
      onCompleted: (data) => {
        toast("Added successfully");
        navigate(`/post/${data.createPost.id}`);
      },
    }
  );
  const onSubmit = (data: Partial<Post>) => {
    const { title, description, date, image, socials } = data;
    createPost({
      variables: {
        title: title!,
        description: description!,
        date: new Date(date).toString(),
        image: "",
        socials,
      },
    });
  };
  return (
    <PageContainer title="Create Post">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FieldContainer>
          <TextField
            fullWidth
            id="outlined-basic"
            label="Name"
            variant="outlined"
            {...register("title")}
          />
          <p>{errors.title?.message}</p>
          <Controller
            control={control}
            {...register("date")}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <ZonedDateTimePicker
                format="YYYY/MM/DD HH:mm"
                ampm={false}
                defaultValue={dayjs(new Date(value))}
                label="Publish Date"
                // @ts-ignore
                onChange={onChange} // send value to hook form
              />
            )}
          />
          <p>{errors.date?.message}</p>
          <Note
            message={`Note: you can schedule news posts by picking a date in the future - just publish the item as normal - the app won't load the news until the scheduled date and TIME`}
            style={{ marginTop: -40, marginBottom: 20 }}
          />
          <TextField
            fullWidth
            id="outlined-basic"
            label="Description"
            variant="outlined"
            multiline={true}
            {...register("description")}
          />
          <p>{errors.description?.message}</p>
          <Controller
            control={control}
            name="socials"
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <SocialLinks
                data={value}
                // @ts-ignore
                onChange={onChange}
                errors={errors.socials}
              />
            )}
          />
          <ErrorRenderer errors={errors} />
          <ButtonContainer>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </ButtonContainer>
        </FieldContainer>
      </form>
    </PageContainer>
  );
};
